import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Users,
  Globe,
  Server,
  Megaphone,
  Building2,
  ShieldCheck,
  Cpu,
  Settings2,
  RotateCcw,
  Pencil,
  Check,
  X,
  Info,
  ChevronDown,
  ChevronRight,
  TrendingDown,
  TrendingUp,
  AlertCircle,
} from "lucide-react";

const ICON_MAP = { Users, Globe, Server, Megaphone, Building2, ShieldCheck, Cpu };

const usd = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const usd2 = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

export default function FixedCostsBreakdown({ onTotalChange }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openCats, setOpenCats] = useState({});
  const [editingLine, setEditingLine] = useState(null); // { key, value, rationale }
  const [overrideMode, setOverrideMode] = useState(false);
  const [overrideDraft, setOverrideDraft] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/economics/fixed-cost-config");
      setConfig(r.data);
      if (Object.keys(openCats).length === 0) {
        const initial = {};
        (r.data.categories || []).forEach((c, i) => (initial[c.key] = i < 2));
        setOpenCats(initial);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, []);

  const linesByCategory = useMemo(() => {
    const map = {};
    (config?.lines || []).forEach((l) => {
      (map[l.category_key] = map[l.category_key] || []).push(l);
    });
    return map;
  }, [config]);

  const updateVariable = async (varKey, optKey) => {
    try {
      const r = await api.patch("/admin/economics/variables", { variables: { [varKey]: optKey } });
      setConfig((c) => ({
        ...c,
        variables: r.data.variables,
        lines: r.data.lines,
        categories: r.data.categories,
        base_total: r.data.base_total,
        effective_total: r.data.effective_total,
      }));
      if (onTotalChange) {
        onTotalChange({
          effective_total: r.data.effective_total,
          base_total: r.data.base_total,
          manual_override: config?.manual_override,
        });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveLineEdit = async () => {
    if (!editingLine) return;
    try {
      const r = await api.patch(`/admin/economics/fixed-cost-lines/${editingLine.key}`, {
        monthly_usd: parseFloat(editingLine.value) || 0,
        rationale: editingLine.rationale,
      });
      setConfig((c) => ({
        ...c,
        lines: r.data.lines,
        categories: r.data.categories,
        base_total: r.data.base_total,
        effective_total: r.data.effective_total,
      }));
      if (onTotalChange) {
        onTotalChange({
          effective_total: r.data.effective_total,
          base_total: r.data.base_total,
          manual_override: config?.manual_override,
        });
      }
      setEditingLine(null);
      toast.success("Cost line updated.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset all fixed-cost lines and variables to industry defaults?")) return;
    try {
      await api.post("/admin/economics/fixed-cost-lines/reset");
      toast.success("Restored industry-default fixed costs.");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveOverride = async () => {
    const val = overrideDraft.trim() === "" ? null : parseFloat(overrideDraft);
    try {
      await api.patch("/admin/economics/config", { fixed_costs_per_month: val });
      toast.success(val == null ? "Manual override cleared." : "Manual override saved.");
      setOverrideMode(false);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading || !config) {
    return <div className="text-sm text-slate-500">Loading fixed-cost model…</div>;
  }

  const overrideActive = config.manual_override != null;
  const effectiveShown = overrideActive ? config.manual_override : config.effective_total;
  const variance = config.effective_total - config.base_total;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white" data-testid="fixed-costs-breakdown">
      {/* Header / total */}
      <div className="border-b border-slate-200 px-5 py-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
            Monthly fixed costs · structured cost model
          </div>
          <div className="font-display text-3xl font-bold text-slate-900 tabular-nums">{usd(effectiveShown)}</div>
          <div className="mt-1 text-xs text-slate-500 flex items-center gap-2 flex-wrap">
            <span>Base (industry defaults): <span className="font-semibold text-slate-700">{usd(config.base_total)}</span></span>
            <span className="text-slate-300">·</span>
            <span className={`inline-flex items-center gap-1 font-semibold ${variance < 0 ? "text-emerald-700" : variance > 0 ? "text-rose-700" : "text-slate-500"}`}>
              {variance < 0 ? <TrendingDown className="w-3 h-3" /> : variance > 0 ? <TrendingUp className="w-3 h-3" /> : null}
              Variables impact: {variance >= 0 ? "+" : ""}{usd(variance)}
            </span>
            {overrideActive && (
              <>
                <span className="text-slate-300">·</span>
                <span className="inline-flex items-center gap-1 text-amber-700 font-semibold">
                  <AlertCircle className="w-3 h-3" /> Manual override active (model total: {usd(config.effective_total)})
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!overrideMode ? (
            <button
              type="button"
              onClick={() => {
                setOverrideDraft(overrideActive ? String(config.manual_override) : "");
                setOverrideMode(true);
              }}
              data-testid="fixed-costs-override-toggle"
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200"
            >
              <Pencil className="w-3.5 h-3.5" /> {overrideActive ? "Edit override" : "Manual override"}
            </button>
          ) : (
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                value={overrideDraft}
                onChange={(e) => setOverrideDraft(e.target.value)}
                placeholder="Leave blank to use model"
                className="w-44 rounded-xl border border-slate-200 px-3 py-1.5 text-sm tabular-nums"
                data-testid="fixed-costs-override-input"
              />
              <button
                type="button"
                onClick={saveOverride}
                data-testid="fixed-costs-override-save"
                className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold bg-slate-900 text-white hover:bg-slate-800"
              >
                <Check className="w-3.5 h-3.5" /> Save
              </button>
              <button
                type="button"
                onClick={() => setOverrideMode(false)}
                className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={resetAll}
            data-testid="fixed-costs-reset"
            className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset defaults
          </button>
        </div>
      </div>

      {/* Variables tool */}
      <div className="border-b border-slate-200 px-5 py-4 bg-gradient-to-br from-slate-50 to-white">
        <div className="flex items-center gap-2 mb-3">
          <Settings2 className="w-4 h-4 text-slate-700" />
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700">Variables — model alternative cost structures</div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="fixed-costs-variables">
          {Object.entries(config.variable_options || {}).map(([varKey, v]) => {
            const Icon = ICON_MAP[v.icon] || Settings2;
            const current = config.variables?.[varKey];
            return (
              <div key={varKey} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`var-${varKey}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="w-3.5 h-3.5 text-slate-600" />
                  <div className="text-xs font-bold text-slate-900">{v.label}</div>
                </div>
                <div className="text-[11px] text-slate-500 mb-2 leading-snug">{v.description}</div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(v.options).map(([optKey, opt]) => {
                    const active = current === optKey;
                    return (
                      <button
                        key={optKey}
                        type="button"
                        onClick={() => updateVariable(varKey, optKey)}
                        data-testid={`var-${varKey}-opt-${optKey}`}
                        className={`text-[11px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${
                          active
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cost line breakdown by category */}
      <div className="divide-y divide-slate-100" data-testid="fixed-costs-categories">
        {(config.categories || []).map((cat) => {
          const lines = linesByCategory[cat.key] || [];
          const open = !!openCats[cat.key];
          const pctOfTotal = config.effective_total > 0 ? Math.round((cat.effective_total / config.effective_total) * 100) : 0;
          return (
            <div key={cat.key} data-testid={`category-${cat.key}`}>
              <button
                type="button"
                onClick={() => setOpenCats((s) => ({ ...s, [cat.key]: !s[cat.key] }))}
                className="w-full px-5 py-3 flex items-center gap-3 hover:bg-slate-50 text-left"
              >
                {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-slate-900">{cat.label}</div>
                  <div className="text-[11px] text-slate-500 truncate">{cat.blurb}</div>
                </div>
                <div className="hidden sm:flex items-center gap-2 w-40">
                  <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-slate-900" style={{ width: `${pctOfTotal}%` }} />
                  </div>
                  <div className="text-[11px] tabular-nums text-slate-500 w-9 text-right">{pctOfTotal}%</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold tabular-nums text-slate-900">{usd(cat.effective_total)}</div>
                  {Math.abs(cat.multiplier - 1) > 0.001 && (
                    <div className={`text-[10px] tabular-nums ${cat.multiplier < 1 ? "text-emerald-600" : "text-rose-600"}`}>
                      ×{cat.multiplier.toFixed(2)} from base {usd(cat.base_total)}
                    </div>
                  )}
                </div>
              </button>
              {open && (
                <div className="px-5 pb-4 pt-1 bg-slate-50/50">
                  {cat.multiplier_sources && cat.multiplier_sources.length > 0 && (
                    <div className="mb-2 text-[11px] text-slate-500 inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2 py-1">
                      <Info className="w-3 h-3" /> {cat.multiplier_sources.join(" · ")}
                    </div>
                  )}
                  <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="text-left px-3 py-2">Line item</th>
                          <th className="text-right px-3 py-2 hidden md:table-cell">Base / mo</th>
                          <th className="text-right px-3 py-2">Effective / mo</th>
                          <th className="px-3 py-2 w-16"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {lines.map((line) => {
                          const isEditing = editingLine?.key === line.key;
                          return (
                            <tr key={line.key} data-testid={`line-${line.key}`}>
                              <td className="px-3 py-2.5 align-top">
                                <div className="font-semibold text-slate-900 text-[13px]">{line.label}</div>
                                {isEditing ? (
                                  <textarea
                                    value={editingLine.rationale}
                                    onChange={(e) => setEditingLine({ ...editingLine, rationale: e.target.value })}
                                    rows={2}
                                    className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1 text-[11px]"
                                    data-testid={`line-${line.key}-rationale-input`}
                                  />
                                ) : (
                                  <div className="text-[11px] text-slate-500 leading-snug mt-0.5">{line.rationale}</div>
                                )}
                                {line.applied_note && !isEditing && (
                                  <div className="text-[10px] text-amber-700 mt-1 inline-flex items-center gap-1">
                                    <Info className="w-2.5 h-2.5" /> {line.applied_note}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-right tabular-nums text-slate-500 hidden md:table-cell align-top">
                                {isEditing ? (
                                  <input
                                    type="number"
                                    step="100"
                                    value={editingLine.value}
                                    onChange={(e) => setEditingLine({ ...editingLine, value: e.target.value })}
                                    className="w-28 rounded-lg border border-slate-200 px-2 py-1 text-right tabular-nums"
                                    data-testid={`line-${line.key}-input`}
                                  />
                                ) : (
                                  usd2(line.monthly_usd)
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-slate-900 align-top">
                                {usd2(line.effective_monthly_usd)}
                              </td>
                              <td className="px-3 py-2.5 text-right align-top">
                                {isEditing ? (
                                  <div className="inline-flex items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={saveLineEdit}
                                      data-testid={`line-${line.key}-save`}
                                      className="rounded-full p-1 bg-slate-900 text-white hover:bg-slate-800"
                                      title="Save"
                                    >
                                      <Check className="w-3 h-3" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setEditingLine(null)}
                                      className="rounded-full p-1 bg-slate-100 hover:bg-slate-200"
                                      title="Cancel"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setEditingLine({
                                        key: line.key,
                                        value: line.monthly_usd,
                                        rationale: line.rationale || "",
                                      })
                                    }
                                    data-testid={`line-${line.key}-edit`}
                                    className="rounded-full p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700"
                                    title="Edit line"
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
